import React, { useState, useEffect, useCallback } from 'react';
import { Star, MessageSquare, ChevronDown } from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const StarRow = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-100'}`} />
    ))}
  </div>
);

const ReplyForm = ({ reviewId, existing, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(existing || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/creator/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: text.trim() }),
      });
      if (r.ok) { onSaved(text.trim()); setOpen(false); }
    } catch {}
    setSaving(false);
  };

  return (
    <div className="mt-3">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 text-xs text-violet-600 font-semibold hover:text-violet-700"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {existing ? 'Edit reply' : 'Reply to this review'}
        </button>
      ) : (
        <div className="bg-violet-50 rounded-xl p-3 mt-2">
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={2}
            placeholder="Write a public reply…"
            className="w-full bg-white border border-violet-100 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
          <div className="flex gap-2 mt-2 justify-end">
            <button onClick={() => setOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving || !text.trim()}
              className="px-4 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Post reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewCard = ({ review: initial }) => {
  const [review, setReview] = useState(initial);

  return (
    <div className="px-5 py-5 border-b border-gray-50 last:border-0">
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {review.user_name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{review.user_name}</span>
            <StarRow rating={review.rating} />
            {review.is_verified_purchase && (
              <span className="text-[10px] bg-emerald-50 text-emerald-600 font-semibold px-2 py-0.5 rounded-full">Verified purchase</span>
            )}
          </div>
          {review.template_name && (
            <p className="text-xs text-gray-400 mt-0.5">on {review.template_name}</p>
          )}
          {review.comment && (
            <p className="text-sm text-gray-600 mt-2">{review.comment}</p>
          )}

          {/* Creator reply */}
          {review.creator_reply && (
            <div className="mt-3 pl-3 border-l-2 border-violet-200">
              <p className="text-xs text-violet-500 font-semibold mb-1">Your reply:</p>
              <p className="text-sm text-gray-600">{review.creator_reply}</p>
            </div>
          )}

          <ReplyForm
            reviewId={review.id}
            existing={review.creator_reply}
            onSaved={(text) => setReview(r => ({ ...r, creator_reply: text }))}
          />
        </div>
        <p className="text-xs text-gray-400 flex-shrink-0">
          {new Date(review.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

const CreatorReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const load = useCallback(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/creator/reviews?page=${page}&limit=${LIMIT}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { items: [], total: 0, template_stats: {} })
      .then(d => {
        setReviews(d.items || []);
        setTotal(d.total || 0);
        setStats(d.template_stats || {});
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '—';

  return (
    <CreatorLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">Reviews</h1>
        {total > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-xl">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span className="text-sm font-bold text-gray-900">{avgRating}</span>
            <span className="text-xs text-gray-400">({total} reviews)</span>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16">
            <Star className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No reviews yet</p>
            <p className="text-xs text-gray-400 mt-1">Once buyers review your templates, they'll appear here.</p>
          </div>
        ) : (
          <>
            {reviews.map(rev => <ReviewCard key={rev.id} review={rev} />)}

            {total > LIMIT && (
              <div className="px-5 py-4 border-t border-gray-50 flex items-center justify-between">
                <span className="text-xs text-gray-400">Showing {reviews.length} of {total}</span>
                <div className="flex gap-2">
                  {page > 1 && (
                    <button
                      onClick={() => setPage(p => p - 1)}
                      className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      Previous
                    </button>
                  )}
                  {reviews.length === LIMIT && (
                    <button
                      onClick={() => setPage(p => p + 1)}
                      className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-1"
                    >
                      Load more <ChevronDown className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </CreatorLayout>
  );
};

export default CreatorReviews;
